import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import toast from 'react-hot-toast';
import { executingAgentService } from '../services';

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'status';
  timestamp: Date;
  crewId?: number;
  isStreaming?: boolean;
  isStatus?: boolean;
}

interface CrewSpecificData {
  messages: Message[];
  sessionId: string | null;
  selectedKnowledgeSources: number[];
  ragConfig: {
    rag_top_k: number;
    rag_similarity_threshold: number;
    rag_max_context_length: number;
    rag_tags: string[];
  };
}

interface ConversationState {
  messages: Message[];
  isStreaming: boolean;
  currentCrewId: number | null;
  sessionId: string | null;
  crewData: Record<number, CrewSpecificData>;
}

type ConversationAction =
  | { type: 'ADD_MESSAGE'; payload: Message }
  | {
      type: 'UPDATE_STATUS_MESSAGE';
      payload: { content: string; crewId: number };
    }
  | { type: 'UPDATE_MESSAGE'; payload: { id: string; content: string } }
  | { type: 'SET_STREAMING'; payload: boolean }
  | { type: 'SET_CURRENT_CREW'; payload: number | null }
  | { type: 'SET_SESSION_ID'; payload: string | null }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'REMOVE_MESSAGE'; payload: string }
  | { type: 'CLEAR_STATUS_MESSAGES' }
  | {
      type: 'SET_CREW_KNOWLEDGE_SOURCES';
      payload: { crewId: number; knowledgeSourceIds: number[] };
    }
  | {
      type: 'SET_CREW_RAG_CONFIG';
      payload: { crewId: number; ragConfig: CrewSpecificData['ragConfig'] };
    }
  | {
      type: 'LOAD_CREW_DATA';
      payload: { crewId: number; data: CrewSpecificData };
    };

interface ConversationContextType extends ConversationState {
  sendMessage: (content: string, conversationId: number) => Promise<void>;
  sendMessageStream: (
    content: string,
    conversationId: number,
    ragKnowledgeSourceIds?: number[],
    ragConfig?: {
      rag_top_k: number;
      rag_similarity_threshold: number;
      rag_max_context_length: number;
      rag_tags: string[];
    }
  ) => Promise<void>;
  clearConversation: () => void;
  setCurrentCrew: (crewId: number | null) => void;
  removeMessage: (messageId: string) => void;
  updateStatusMessage: (content: string, crewId: number) => void;
  clearStatusMessages: () => void;
  setCrewKnowledgeSources: (
    crewId: number,
    knowledgeSourceIds: number[]
  ) => void;
  setCrewRagConfig: (
    crewId: number,
    ragConfig: CrewSpecificData['ragConfig']
  ) => void;
  getCrewData: (crewId: number) => CrewSpecificData | null;
}

const getDefaultCrewData = (): CrewSpecificData => ({
  messages: [],
  sessionId: null,
  selectedKnowledgeSources: [],
  ragConfig: {
    rag_top_k: 10,
    rag_similarity_threshold: 0.6,
    rag_max_context_length: 2000,
    rag_tags: [],
  },
});

const initialState: ConversationState = {
  messages: [],
  isStreaming: false,
  currentCrewId: null,
  sessionId: null,
  crewData: {},
};

function conversationReducer(
  state: ConversationState,
  action: ConversationAction
): ConversationState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'UPDATE_STATUS_MESSAGE':
      // Remove all existing status messages first
      const messagesWithoutStatus = state.messages.filter(
        msg => msg.role !== 'status'
      );

      // Add the new status message
      const statusMessage: Message = {
        id: `status_${action.payload.crewId}`,
        content: action.payload.content,
        role: 'status',
        timestamp: new Date(),
        crewId: action.payload.crewId,
        isStatus: true,
      };

      return { ...state, messages: [...messagesWithoutStatus, statusMessage] };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.payload.id
            ? { ...msg, content: action.payload.content }
            : msg
        ),
      };
    case 'SET_STREAMING':
      return { ...state, isStreaming: action.payload };
    case 'SET_CURRENT_CREW':
      return { ...state, currentCrewId: action.payload };
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };
    case 'CLEAR_MESSAGES':
      return { ...state, messages: [], sessionId: null };
    case 'REMOVE_MESSAGE':
      return {
        ...state,
        messages: state.messages.filter(msg => msg.id !== action.payload),
      };
    case 'CLEAR_STATUS_MESSAGES':
      return {
        ...state,
        messages: state.messages.filter(msg => !msg.isStatus),
      };
    case 'SET_CREW_KNOWLEDGE_SOURCES':
      return {
        ...state,
        crewData: {
          ...state.crewData,
          [action.payload.crewId]: {
            ...(state.crewData[action.payload.crewId] || getDefaultCrewData()),
            selectedKnowledgeSources: action.payload.knowledgeSourceIds,
          },
        },
      };
    case 'SET_CREW_RAG_CONFIG':
      return {
        ...state,
        crewData: {
          ...state.crewData,
          [action.payload.crewId]: {
            ...(state.crewData[action.payload.crewId] || getDefaultCrewData()),
            ragConfig: action.payload.ragConfig,
          },
        },
      };
    case 'LOAD_CREW_DATA':
      return {
        ...state,
        crewData: {
          ...state.crewData,
          [action.payload.crewId]: action.payload.data,
        },
        messages: action.payload.data.messages,
        sessionId: action.payload.data.sessionId,
      };
    default:
      return state;
  }
}

const ConversationContext = createContext<ConversationContextType | undefined>(
  undefined
);

export function ConversationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(conversationReducer, initialState);

  const sendMessage = async (
    content: string,
    conversationId: number
  ): Promise<void> => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date(),
      crewId: conversationId,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });

    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      crewId: conversationId,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
    dispatch({ type: 'SET_STREAMING', payload: true });

    try {
      // Executar crew com RAG - usar o novo método
      const stream = await executingAgentService.executeCrewRagStreamString(
        conversationId,
        content,
        conversationId // crewId é o mesmo que conversationId neste contexto
      );

      let fullContent = '';
      for await (const chunk of stream) {
        fullContent += chunk;
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: { id: assistantMessage.id, content: fullContent },
        });
      }

      // Atualizar session ID se necessário
      if (conversationId && conversationId.toString() !== state.sessionId) {
        dispatch({
          type: 'SET_SESSION_ID',
          payload: conversationId.toString(),
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro ao executar conversa';
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { id: assistantMessage.id, content: `Erro: ${errorMessage}` },
      });
      toast.error(errorMessage);
    } finally {
      dispatch({ type: 'SET_STREAMING', payload: false });
    }
  };

  const sendMessageStream = async (
    content: string,
    conversationId: number,
    ragKnowledgeSourceIds?: number[],
    ragConfig?: {
      rag_top_k: number;
      rag_similarity_threshold: number;
      rag_max_context_length: number;
      rag_tags: string[];
    }
  ): Promise<void> => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date(),
      crewId: conversationId,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });

    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      crewId: conversationId,
      isStreaming: true,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
    dispatch({ type: 'CLEAR_STATUS_MESSAGES' }); // Clear any existing status messages
    dispatch({ type: 'SET_STREAMING', payload: true });

    try {
      // Configuração RAG será passada diretamente para o serviço

      // Usar o novo método com RAG
      const stream = await executingAgentService.executeCrewRagStreamString(
        conversationId,
        content,
        conversationId // crewId é o mesmo que conversationId neste contexto
      );

      let fullContent = '';
      let hasReceivedMessage = false;

      for await (const chunk of stream) {
        try {
          // Try to parse as JSON first
          const data = JSON.parse(chunk);

          if (data.type === 'status') {
            // Ignore status messages completely - never show them in the conversation
            // Do nothing, just skip to next chunk
          } else if (data.type === 'message') {
            // This is the final message content - extract only the message field
            hasReceivedMessage = true;
            fullContent = data.message; // Only the message content, not the full JSON
            dispatch({
              type: 'UPDATE_MESSAGE',
              payload: { id: assistantMessage.id, content: fullContent },
            });
            // Stop processing after receiving the final message
            break;
          }
        } catch (error) {
          // If JSON parsing fails, check for STATUS: prefix (legacy format)
          // Ignore status messages completely - never show them in the conversation
          if (chunk.startsWith('STATUS:')) {
            // Do nothing, just skip status messages
          } else if (!hasReceivedMessage) {
            // This is regular message content
            fullContent += chunk;
            dispatch({
              type: 'UPDATE_MESSAGE',
              payload: { id: assistantMessage.id, content: fullContent },
            });
          }
        }
      }

      // Atualizar session ID se necessário
      if (conversationId && conversationId.toString() !== state.sessionId) {
        dispatch({
          type: 'SET_SESSION_ID',
          payload: conversationId.toString(),
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro ao executar conversa';
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { id: assistantMessage.id, content: `Erro: ${errorMessage}` },
      });
      toast.error(errorMessage);
    } finally {
      dispatch({ type: 'SET_STREAMING', payload: false });
    }
  };

  const clearConversation = useCallback((): void => {
    dispatch({ type: 'CLEAR_MESSAGES' });
  }, []);

  const setCurrentCrew = useCallback((crewId: number | null): void => {
    dispatch({ type: 'SET_CURRENT_CREW', payload: crewId });
  }, []);

  const removeMessage = useCallback((messageId: string): void => {
    dispatch({ type: 'REMOVE_MESSAGE', payload: messageId });
  }, []);

  const setCrewKnowledgeSources = useCallback(
    (crewId: number, knowledgeSourceIds: number[]): void => {
      dispatch({
        type: 'SET_CREW_KNOWLEDGE_SOURCES',
        payload: { crewId, knowledgeSourceIds },
      });
    },
    []
  );

  const setCrewRagConfig = useCallback(
    (crewId: number, ragConfig: CrewSpecificData['ragConfig']): void => {
      dispatch({ type: 'SET_CREW_RAG_CONFIG', payload: { crewId, ragConfig } });
    },
    []
  );

  const getCrewData = useCallback(
    (crewId: number): CrewSpecificData | null => {
      return state.crewData[crewId] || null;
    },
    [state.crewData]
  );

  const updateStatusMessage = useCallback(
    (content: string, crewId: number): void => {
      dispatch({ type: 'UPDATE_STATUS_MESSAGE', payload: { content, crewId } });
    },
    []
  );

  const clearStatusMessages = useCallback((): void => {
    dispatch({ type: 'CLEAR_STATUS_MESSAGES' });
  }, []);

  const value: ConversationContextType = {
    ...state,
    sendMessage,
    sendMessageStream,
    clearConversation,
    setCurrentCrew,
    removeMessage,
    updateStatusMessage,
    clearStatusMessages,
    setCrewKnowledgeSources,
    setCrewRagConfig,
    getCrewData,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation(): ConversationContextType {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error(
      'useConversation must be used within a ConversationProvider'
    );
  }
  return context;
}
